import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Icon,
  InlineMessage,
  RadioButton,
  Typography,
} from '@saviynt/design-system';

import { certsExportApplyFilter } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';

import { formatFilters, formatRows } from './CertsExportsUtils';
import { invertedMessages } from './messages';

function WhatToExport() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { messages } = useSelector((state) => state?.user) || {};
  const {
    certsExport: { applyFilter, certsDetailsAllCount } = {},
    core: { searchText, campaignType } = {},
    global: { viewRowsList } = {},
    saviCompanion: { appliedSaviFilters } = {},
    viewControls: { advancedFilters, rowsFilters } = {},
  } = useSelector((state) => state.certifications?.details || {});

  const l = useInvertedLocalize(invertedMessages);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;

  const handleRadioButtonClick = (e, value) => {
    e.stopPropagation();
    dispatch(certsExportApplyFilter(value === 'onlyFilteredResults'));
  };

  const whatToExportLabel =
    messages.CERTS_EXPORT_WHAT_TO_EXPORT ||
    localize(intl, getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT'));
  const allItemsLabel =
    messages.CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS ||
    localize(intl, getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS'));
  const onlyFiltererdResultsLabel =
    messages.CERTS_EXPORT_WHAT_TO_EXPORT_ONLY_FILTERED_RESULTS ||
    localize(
      intl,
      getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT_ONLY_FILTERED_RESULTS')
    );
  const noRecordText =
    messages.CERTS_EXPORT_WHAT_TO_EXPORT_NO_RECORD ||
    localize(intl, getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT_NO_RECORD'));
  const smartFilterLabel =
    messages.CERTS_SMART_FILTERS ||
    localize(intl, getMessageKey('CERTS_SMART_FILTERS'));
  const saviFiltersLabel =
    messages.CERTS_SAVI_FILTERS ||
    localize(intl, getMessageKey('CERTS_SAVI_FILTERS'));
  const filterAppliedLabel =
    messages.CERTS_EXPORT_WHAT_TO_EXPORT_FILTER_APPLIED ||
    localize(intl, getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT_FILTER_APPLIED'));

  const showRowsText =
    viewRowsList?.length > 1 && rowsFilters?.length !== viewRowsList?.length;

  const filtersText = formatFilters(advancedFilters);
  const rowsText =
    showRowsText && viewRowsList?.length > 1
      ? formatRows(isEOCampaign, rowsFilters)
      : '';
  const { smartFilter, actionFilters } = appliedSaviFilters || {};
  const smartFilterText = smartFilter
    ? `${smartFilterLabel} : ${smartFilter}`
    : '';
  const actionFilterText = actionFilters?.length
    ? `${saviFiltersLabel} : ${actionFilters.join(', ')}`
    : '';

  const descriptionText = [
    rowsText,
    filtersText,
    smartFilterText,
    actionFilterText,
  ]
    .filter(Boolean)
    .join(', ');

  const showDescription =
    showRowsText || filtersText || smartFilter?.length || actionFilters?.length;

  const getDescription = () => (
    <>
      {showDescription ? (
        <div className='CertsWhatToExport-radioTextWrapper'>
          <Typography kind='body2'>
            <>
              <Typography kind='body2-bold'>{`${filterAppliedLabel} -`}</Typography>
              {descriptionText}
            </>
          </Typography>
        </div>
      ) : null}

      {searchText?.length ? (
        <div className='CertsExportModalBody-radioButtons-description-searchText'>
          <Typography kind='body2-bold'>{l('Search applied')} - </Typography>
          <Typography kind='body2'>{searchText}</Typography>
        </div>
      ) : null}
    </>
  );

  const isOnlyFilteredItemsRadioButtonDisabled =
    !showRowsText &&
    !filtersText &&
    !searchText &&
    !smartFilter?.length &&
    !actionFilters?.length;

  const radioValue = applyFilter ? 'onlyFilteredResults' : 'allItems';

  useEffect(() => {
    if (certsDetailsAllCount === 0) {
      dispatch(certsExportApplyFilter(false));
    }
  }, [certsDetailsAllCount]);

  return (
    <Box className='WhatToExport'>
      <Typography kind='h3'>{whatToExportLabel}</Typography>

      <Box className='CertsExportModalBody-radioButtons'>
        <RadioButton
          id='allItems'
          name='radioGroup1'
          title={l('All Items')}
          value='allItems'
          label={allItemsLabel}
          radioSelection={radioValue}
          onClick={handleRadioButtonClick}
        />
        <RadioButton
          id='onlyFilteredItems'
          name='radioGroup1'
          title={l('Only Filtered Results')}
          description={getDescription()}
          value='onlyFilteredResults'
          label={onlyFiltererdResultsLabel}
          radioSelection={radioValue}
          onClick={handleRadioButtonClick}
          isDisabled={
            isOnlyFilteredItemsRadioButtonDisabled || certsDetailsAllCount === 0
          }
        />
        <InlineMessage
          kind='background'
          text={noRecordText}
          colorTheme='info'
          leftIcon={
            <Icon
              kind='AlertInfoFilled'
              color='--color-foreground-info-medium'
            />
          }
          size='small'
          isVisible={certsDetailsAllCount === 0}
        />
      </Box>
    </Box>
  );
}

export default WhatToExport;
